/**
 * RECRUIT RAMP-UP: 1-Week Re-Entry Program
 *
 * Purpose: Entry-level on-ramp for someone brand-new to structured
 * training OR returning after 30+ days off with significant capacity lost.
 *
 * This is NOT a tactical/operator protocol. It is the gentlest possible
 * structured entry: bodyweight movement patterns first, simple resistance
 * second, easy aerobic exposure throughout. Equipment-light (bodyweight
 * + optional light dumbbells).
 *
 * WEEK 1 — RE-ENTRY (4 days)
 *   Day 1: Full Body — Movement Patterns (squat, push, pull, bridge)
 *   Day 3: Lower + Core (goblet squat, RDL, glute bridge, dead bug)
 *   Day 5: Upper + Carry (shoulder press, pulldown, push-up, farmer carry)
 *   Day 7: Readiness Check — baseline numbers to launch next program
 *
 * Goal: RPE 5-6, RIR 4+. Every session ~30-40 minutes. Re-establish
 * movement quality and finish each session feeling fresher than you
 * started.
 */

import type {
  WorkoutProgram,
  WorkoutTemplateDay,
  ProgramMetadata,
  ProgramPeriodizationModel,
} from '../types/program';

// ============================================================================
// METADATA
// ============================================================================

const metadata: ProgramMetadata = {
  id: 'recruit-ramp-up-1week',
  name: 'RECRUIT RAMP-UP: 1-Week Re-Entry',
  description:
    'Beginner-level 1-week on-ramp for first-time trainees or returners after 30+ days off. Four bodyweight-forward sessions build movement quality, simple resistance tolerance, and aerobic readiness. Ends with a baseline readiness check to launch the next program.',
  level: 'recruit',
  experienceLevel: 'beginner',
  duration: 1,
  daysPerWeek: 4,
  goals: ['movement-quality', 'aerobic-base', 'return-to-training'],
  difficulty: 'beginner',
  tags: ['ramp-up', 're-entry', 'beginner', 'return-to-training'],
  emphasis: 'Bodyweight movement quality → simple resistance → readiness',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  usesPeriodization: true,
  hasRangeDay: false,
  requiredEquipment: 'minimal',
  // Beginner / heavy-detraining reentry. Very modest target — 45 min/wk
  // (≈3 × 15 min walks). The point is to establish the habit, not to
  // chase aerobic adaptation yet.
  weeklyZ2Target: 45,
};

// ============================================================================
// PERIODIZATION
// ============================================================================

const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    {
      weeks: [1],
      type: 'accumulation',
      name: 'Re-Entry — Movement & Readiness',
      focus: 'Bodyweight patterns first, light dumbbells second. Slow tempo, full ROM, easy aerobic exposure. RPE 5-6 (RIR 4+). No grinding — every set should end clean.',
      intensityMultiplier: 0.55,
      volumeMultiplier: 0.5,
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 0.5,
        restPeriod: '60-90s',
      },
    },
  ],
};

// ============================================================================
// WEEK 1: 4 training days (Day 1, 3, 5, 7) + 3 rest (Day 2, 4, 6)
// ============================================================================

// --- W1 DAY 1: FULL BODY — Movement Patterns ---
const w1Day1: WorkoutTemplateDay = {
  name: 'Full Body — Movement Patterns',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Easy walk or bike — just raise your temperature' },
        { name: 'Cat-Cow', sets: 1, reps: 10, format: 'prep' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep' },
        { name: 'Ankle Rocks', sets: 1, reps: '8/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Squat Pattern — Tempo',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 3, reps: 8, intensity: 'bodyweight', rest: '60s', details: 'Bodyweight only. 3-sec descent. Sit between your hips, knees track over toes.' },
      ],
    },
    {
      name: 'Push + Pull',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Push-Ups', sets: 3, reps: '5-8', intensity: 'bodyweight', rest: '0:00', details: 'Knee or incline acceptable. Choose the version where you can hit 5 clean reps with a 2-sec descent.' },
        { name: 'Dumbbell Row', sets: 3, reps: '8/side', intensity: 'light', rest: '60s', details: 'Light DB. Pause at the top — feel the shoulder blade pull in.' },
      ],
    },
    {
      name: 'Glute Activation',
      type: 'accessory',
      exercises: [
        { name: 'Glute Bridge', sets: 2, reps: 12, rest: '45s', details: 'Squeeze glutes hard at the top, 2-sec pause.' },
      ],
    },
    {
      name: 'Zone 2 Walk',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '10-15 min', details: 'Easy conversational pace. Outdoors or treadmill — anything that gets you moving.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '45s/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '45s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- REST DAY ---
const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

// --- W1 DAY 3: LOWER + CORE ---
const w1Day3: WorkoutTemplateDay = {
  name: 'Lower + Core',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Easy walk' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep' },
        { name: 'Glute Bridge Iso', sets: 1, reps: '15s + 8 reps', format: 'prep' },
        { name: 'Ankle Rocks', sets: 1, reps: '8/side', format: 'prep' },
        { name: 'Cat-Cow', sets: 1, reps: 8, format: 'prep' },
      ],
    },
    {
      name: 'Squat — Loaded',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 3, reps: 10, intensity: 'light', rest: '75s', details: 'Light DB held at chest. Smooth tempo, no bouncing out of the bottom.' },
      ],
    },
    {
      name: 'Hinge — Tempo',
      type: 'strength',
      exercises: [
        { name: 'Romanian Deadlift', sets: 3, reps: 10, intensity: 'light', rest: '75s', details: 'Light dumbbells. 3-sec eccentric. Hinge from hips, soft knees, push hips back.' },
      ],
    },
    {
      name: 'Single Leg + Core',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Single-Leg Glute Bridge', sets: 2, reps: '8/side', intensity: 'bodyweight', rest: '0:00', details: 'Drive through the heel. Hips level.' },
        { name: 'Dead Bug', sets: 2, reps: '8/side', rest: '60s', details: 'Slow and controlled. Press lower back into the floor the entire time.' },
      ],
    },
    {
      name: 'Zone 2 Walk',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '15 min', details: 'Conversational pace. Build endurance without spiking heart rate.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery' },
        { name: 'Hip Flexor Stretch', duration: '45s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W1 DAY 5: UPPER + CARRY ---
const w1Day5: WorkoutTemplateDay = {
  name: 'Upper + Carry',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Easy walk or bike' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
        { name: 'Band External Rotation', sets: 2, reps: '10/side', format: 'prep' },
        { name: 'Cat-Cow', sets: 1, reps: 8, format: 'prep' },
      ],
    },
    {
      name: 'Press',
      type: 'strength',
      exercises: [
        { name: 'Dumbbell Shoulder Press', sets: 3, reps: 8, intensity: 'light', rest: '75s', details: 'Seated or standing. Press to lockout, lower with control. Stop if form breaks.' },
      ],
    },
    {
      name: 'Pull',
      type: 'strength',
      exercises: [
        { name: 'Lat Pulldown', sets: 3, reps: 10, intensity: 'light', rest: '75s', details: 'Light load. If no cable, use a band pulldown — full ROM, slow return.' },
      ],
    },
    {
      name: 'Push-Up Volume',
      type: 'accessory',
      exercises: [
        { name: 'Push-Ups', sets: 3, reps: 'AMRAP capped at 12', intensity: 'bodyweight', rest: '60s', details: 'Knee or incline if needed. Stop the set at 12 even if you have more — no grinding this week.' },
      ],
    },
    {
      name: 'Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Farmer Carry', sets: 3, reps: '20m', intensity: 'light', rest: '60s', details: 'Light DBs. Tall posture, ribs down, even breathing.' },
      ],
    },
    {
      name: 'Zone 2 Walk',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '15 min', details: 'Conversational pace.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '45s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W1 DAY 7: READINESS CHECK ---
const w1Day7: WorkoutTemplateDay = {
  name: 'Readiness Check — Baseline Numbers',
  sessionType: 'pt',
  estimatedDuration: 30,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Easy walk' },
        { name: 'Cat-Cow', sets: 1, reps: 10, format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
        { name: 'Glute Bridge Iso', sets: 1, reps: '15s + 8 reps', format: 'prep' },
        { name: 'Ankle Rocks', sets: 1, reps: '8/side', format: 'prep' },
      ],
    },
    {
      name: 'Readiness Test — Strength',
      type: 'assessment',
      exercises: [
        { name: 'Push-Ups', sets: 1, reps: 'Max', intensity: 'bodyweight', rest: '3 min', details: 'One set. Strict, full ROM. Count every clean rep. Stop at form breakdown — this is the number we build from.' },
        { name: 'Goblet Squat', sets: 1, reps: 'Max in 60s', intensity: 'bodyweight', rest: '3 min', details: 'Bodyweight only. Count every full-depth rep in 60 seconds. Record the number.' },
      ],
    },
    {
      name: 'Readiness Test — Endurance',
      type: 'assessment',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '5 min', details: 'Steady walk pace test. Find a sustainable pace — note the speed and incline. This is your starting Z2 setting.' },
      ],
    },
    {
      name: 'Readiness Test — Core',
      type: 'assessment',
      exercises: [
        { name: 'Plank', sets: 1, duration: 'Max', intensity: 'bodyweight', rest: '0:00', details: 'Hold to fatigue. Stop when hips drop or form breaks. Record the time.' },
      ],
    },
    {
      name: 'Debrief',
      type: 'recovery',
      exercises: [
        { name: '90/90 Breathing', duration: '3 min', format: 'recovery', details: 'Recover and write down all 4 numbers: max push-ups, squats in 60s, walk pace, plank hold. These are your baselines for week 1 of your next program.' },
        { name: 'Couch Stretch', duration: '45s/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '45s/side', format: 'recovery' },
      ],
    },
  ],
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const recruitRampUp1Week: WorkoutProgram = {
  metadata,
  periodization,
  workouts: [],
  templates: {
    'week1-day1': w1Day1,
    'week1-day2': restDay,
    'week1-day3': w1Day3,
    'week1-day4': restDay,
    'week1-day5': w1Day5,
    'week1-day6': restDay,
    'week1-day7': w1Day7,
  },
};
